// src/components/CountdownTimer.tsx
import React, { useEffect, useState } from 'react'
import { Box, Typography, Paper, useTheme } from '@mui/material'
import { keyframes } from '@emotion/react'
import moment from 'moment-timezone'

interface CountdownTimerProps {
  endDate: Date | string
  timeZone?: string // e.g. "Europe/Kyiv"
}

interface TimeLeft {
  days: number
  hours: number
  minutes: number
  seconds: number
}

const calculateTimeLeft = (end: Date): TimeLeft | null => {
  const diff = end.getTime() - Date.now()
  if (diff <= 0) return null
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  }
}

const pad = (n: number) => n.toString().padStart(2, '0')

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

export const CountdownTimer: React.FC<CountdownTimerProps> = ({ endDate, timeZone }) => {
  const theme = useTheme()

  // parse the incoming UTC string into a Date at that instant
  const targetDate = React.useMemo(() => {
    const m = moment.utc(endDate)
    return timeZone ? m.tz(timeZone).toDate() : m.toDate()
  }, [endDate, timeZone])

  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(calculateTimeLeft(targetDate))

  useEffect(() => {
    const id = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetDate))
    }, 1000)
    return () => clearInterval(id)
  }, [targetDate])

  // if expired, we'll still render 00's but mark isExpired
  const isExpired = timeLeft === null
  const display = timeLeft ?? { days: 0, hours: 0, minutes: 0, seconds: 0 }

  const units = Object.entries(display) as [keyof TimeLeft, number][]

  return (
    <Paper
      variant="outlined"
      sx={{
        width: '100%',
        position: 'relative',
        p: '28px 16px 16px',
        display: 'inline-block',
        textAlign: 'center',
        minWidth: 200,
      }}
    >
      {timeZone && (
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            top: 4,
            right: 10,
            color: theme.palette.custom.grey2,
          }}
        >
          {timeZone}
        </Typography>
      )}

      <Box display="flex" justifyContent="center" flexWrap="wrap" gap={1}>
        {units.map(([unit, val]) => (
          <Box key={unit} sx={{ textAlign: 'center', minWidth: 40 }}>
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                animation: `${fadeInUp} 0.4s ease`,
                fontSize: '1.25rem',
                fontWeight: 600,
                color: isExpired ? theme.palette.error.main : theme.palette.text.primary,
              }}
            >
              {pad(val)}
            </Box>
            <Typography variant="caption" color="textSecondary" component="div">
              {unit}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  )
}
